import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Clock } from 'lucide-react';
import FormDrawer from './FormDrawer';
import { useSystemSettings } from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';

const formatCutoffHourLabel = (hour) => {
  const h = Math.min(23, Math.max(0, Number(hour) || 10));
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:00 ${suffix} UTC+5`;
};

const LeaveModal = ({ isOpen, onClose, onSuccess }) => {
  const { data: systemSettings } = useSystemSettings();
  const cutoffHour =
    systemSettings?.same_day_leave_cutoff_hour ??
    SYSTEM_SETTINGS_DEFAULTS.same_day_leave_cutoff_hour;
  const cutoffLabel = formatCutoffHourLabel(cutoffHour);

  // Same-day leave allowed only before configured cutoff (app timezone / UTC+5)
  const canMarkTodayLeave = useCallback(() => {
    const now = new Date();
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const utcPlus5Time = new Date(utcTime + (5 * 60 * 60 * 1000));
    const currentHour = utcPlus5Time.getHours();
    return currentHour < cutoffHour;
  }, [cutoffHour]);

  const [formData, setFormData] = useState({
    category: '',
    description: '',
    range_type: 'tomorrow',
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const leaveCategories = [
    { value: 'annual', label: 'Annual Leave' },
    { value: 'casual', label: 'Casual Leave' },
    { value: 'medical', label: 'Medical Leave' }
  ];

  const rangeTypes = [
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'this_week', label: 'This Week' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Fetch leave balance when modal opens; move off Today if cutoff has passed
  useEffect(() => {
    if (!isOpen) return;
    fetchLeaveBalance();
    setFormData((prev) => {
      if (!canMarkTodayLeave() && prev.range_type === 'today') {
        return { ...prev, range_type: 'tomorrow' };
      }
      return prev;
    });
  }, [isOpen, canMarkTodayLeave]);

  const fetchLeaveBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await axios.get('/api/leaves/balance');
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balance:', error);
      setLeaveBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const getCategoryRemaining = (category) => {
    if (!leaveBalance) return null;
    
    switch (category) {
      case 'annual':
        return { remaining: leaveBalance.annual_remaining, limit: leaveBalance.annual_limit };
      case 'casual':
        return { remaining: leaveBalance.casual_remaining, limit: leaveBalance.casual_limit };
      case 'medical':
        return { remaining: leaveBalance.medical_remaining, limit: leaveBalance.medical_limit };
      default:
        return null;
    }
  };

  const getCategoryColor = (remaining, limit) => {
    const percentage = (remaining / limit) * 100;
    if (percentage > 50) return 'text-green-600';
    if (percentage > 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.description || !formData.range_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.range_type === 'custom' && (!formData.start_date || !formData.end_date)) {
      toast.error('Please select start and end dates for custom range');
      return;
    }

    // Additional validation for today's leave (uses System Config cutoff)
    if (formData.range_type === 'today' && !canMarkTodayLeave()) {
      toast.error(`Today's leave can only be marked before ${cutoffLabel}. Please select tomorrow or another date.`);
      return;
    }

    // Validate that dates are not in the past
    if (formData.range_type === 'custom') {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      
      if (startDate < today) {
        toast.error('Start date cannot be in the past');
        return;
      }
      
      if (endDate < today) {
        toast.error('End date cannot be in the past');
        return;
      }
      
      if (startDate > endDate) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    // Check leave balance before submitting
    if (leaveBalance && formData.category) {
      const categoryInfo = getCategoryRemaining(formData.category);
      if (categoryInfo && categoryInfo.remaining === 0) {
        toast.error(`No ${formData.category} leave remaining. Please contact HR directly.`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        category: formData.category,
        description: formData.description,
        range_type: formData.range_type
      };

      if (formData.range_type === 'custom') {
        payload.start_date = new Date(formData.start_date).toISOString();
        payload.end_date = new Date(formData.end_date).toISOString();
      }

      await axios.post('/api/leaves', payload);
      toast.success('Leave request submitted successfully');
      onSuccess();
      onClose();
      setFormData({
        category: '',
        description: '',
        range_type: canMarkTodayLeave() ? 'today' : 'tomorrow',
        start_date: '',
        end_date: ''
      });
    } catch (error) {
      console.error('Error submitting leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary"
        disabled={loading}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="leave-form"
        disabled={loading}
        className="btn-primary min-w-[140px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            Submitting…
          </span>
        ) : (
          'Submit Leave'
        )}
      </button>
    </>
  );

  return (
    <FormDrawer
      open={isOpen}
      onClose={onClose}
      title="Mark Leave"
      subtitle="Submit a leave request for approval."
      icon={<Clock className="h-5 w-5" />}
      iconColor="primary"
      widthClass="sm:max-w-md lg:max-w-lg"
      footer={footer}
    >
        <form id="leave-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Leave Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leave Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="">Select category</option>
              {leaveCategories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            
           </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Please provide a reason for your leave..."
              required
            />
          </div>

                               {/* Date Range Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range *
            </label>
            <select
              value={formData.range_type}
              onChange={(e) => handleInputChange('range_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              {rangeTypes.map(type => (
                <option 
                  key={type.value} 
                  value={type.value}
                  disabled={type.value === 'today' && !canMarkTodayLeave()}
                >
                  {type.value === 'today' && !canMarkTodayLeave() 
                    ? `${type.label} (Not available after ${cutoffLabel})` 
                    : type.label}
                </option>
              ))}
            </select>
            {!canMarkTodayLeave() && (
              <p className="text-sm text-orange-600 mt-1">
                Today's leave can only be marked before {cutoffLabel}. You can still mark leave for tomorrow or other dates.
              </p>
            )}
          </div>

          {/* Custom Date Range */}
          {formData.range_type === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Leave Information</h3>
                <p className="text-sm text-blue-700 mt-1">
                  When you mark yourself on leave, your hours won't be affected and your efficiency/utilization metrics won't be penalized for the selected period.
                </p>
              </div>
            </div>
          </div>

        </form>
    </FormDrawer>
  );
};

export default LeaveModal;
